import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dynamicSort } from '../utils/tableUtilities';
import CustomForm from './CustomForm';
import { DefaultButton } from './DefaultButton';
import { FloatingLabel } from './FloatingLabel';
import { Col, Row } from './Grid';
import ReactForm from './ReactForm';
import { withComponentCapabilities } from './capabilities/componentCapabilities';
import { deepMerge } from '../utils/objectUtilities';
import { ensureArray } from '../utils/arrayUtilities';
import Card from './Card';
import { FormDefPreview } from './FormDefPreview';
import { buildInitDelta } from '../utils/formUtilities';
import { customFormQuery } from './CustomCtrl';

/**
 * An editor used to modify the defintion of a DataList in a custom form layout
 * @component
 */
const DataListEditor = ({ tools, value, disabled, onChange }) => {
    const { t } = useTranslation();

    const def = useMemo(() => ([{
        borderless: true,
        rows: [
            ...tools.labelFields(t),
            ...tools.fieldFields(t),
            [ { type: 'json', field: 'sort', rowspan: 1, label: t('defaultSortJSON') } ],
            [
                { type: 'checkbox', field: 'disableAdd', label: t('disableAdd') },
                { type: 'checkbox', field: 'disableRemove', label: t('disableRemove') },
                { type: 'checkbox', field: 'confirmDelete', label: t('confirmDelete') }
            ],
            ...tools.commonFields(t),
        ]
    }]), [t, tools]);


    return <>
        <CustomForm def={def} live={true} data={value} restricted={false} disabled={disabled} required={true} hideLabel={false} readonly={false} onChange={onChange}></CustomForm>
        <Card title={t('rows')}>
            <FormDefPreview placeholder={t('rowDefinition')} value={value?.rowdef} disabled={disabled} onChange={rowdef => onChange({ rowdef })}></FormDefPreview>
            <Row className="align-items-start">
                <Col>
                    <FormDefPreview placeholder={t('footerRowDefinition')} value={value?.footerrowdef} disabled={disabled} onChange={footerrowdef => onChange({ footerrowdef })}></FormDefPreview>
                </Col>
                <Col xs="auto">
                    <FloatingLabel placeholder=" ">
                        <DefaultButton kind="clear" disabled={disabled || !value?.footerrowdef} onClick={() => onChange({ footerrowdef: null})}></DefaultButton>
                    </FloatingLabel>
                </Col>
            </Row>
        </Card>
    </>;
}

export const customCtrlDef = {
    def: () => ({
        control: DataListEditor
    }),
    control: ({ def, value, style, ...props }, { args }) => {
        return <DataList rowDef={def.rowdef} footerRowDef={def.footerrowdef} defaultSort={def.sort} args={args} initialValueMapping={def.initialValueMapping} confirmDelete={def.confirmDelete} disableAdd={def.disableAdd} disableRemove={def.disableRemove} value={value} style={{ maxHeight: '350px', ...def.style, ...style }} {...props}></DataList>
    },
    comparator: async (query, value, { def }) => {
        const matches = await customFormQuery(def.rowdef, ensureArray(value?.value), query?.value);
        return matches?.length > 0
    }
}

const DataListElement = ({ rowIdx, def, row, args, context, disabled, readonly, hideLabel, onChange }) => {
    const handleChange = useCallback((delta) => {
        onChange(delta, rowIdx);
    }, [onChange, rowIdx])
    let ctrl;
    switch (def.type ?? 'form') {
        case 'form':
            ctrl = <CustomForm def={def} data={row} args={args} context={context} disabled={disabled} readonly={readonly} required={true} hideLabel={hideLabel} onChange={handleChange}></CustomForm>;
            break;
        case 'dynamic':
            ctrl = def(row, disabled, hideLabel, onChange);
            break;
        default:
            throw new Error("Unknown data list column type " + def.type);
    }
    return <Col xs={def.colspan}>{ctrl}</Col>
}

/**
 * A dynamically generated list of entries where each entry is represented by a CustomFrom of arbirtrary complexity and size
 * @component
 */
const DataList = withComponentCapabilities(({rowDef, footerRowDef, defaultSort, defaultNewRow, args, context, initialValueMapping, value: inputValue, disabled = false, readonly = false, hideLabel = false, disableAdd = false, disableRemove = false, onChange, onAdd, onUpdate, onRemove, confirmDelete = false}) => {
    const value = useMemo(() => inputValue && Array.isArray(inputValue) ? inputValue : [], [inputValue]);
    const { t } = useTranslation();
    const sort = defaultSort;
    const [newRow, setNewRow] = useState({...defaultNewRow});
    const footerRef = useRef();

    const def = useMemo(() => Array.isArray(rowDef) && !rowDef[0]?.rows ? rowDef : [rowDef], [rowDef]);
    const footerDef = useMemo(() => footerRowDef ? (Array.isArray(footerRowDef) && !footerRowDef[0]?.rows ? footerRowDef : [footerRowDef]) : footerRowDef, [footerRowDef]);

    const displayRows = useMemo(() => {
        const displayRows = [...ensureArray(value)];
        displayRows.sort(dynamicSort(sort));
        return displayRows;
    }, [value, sort]);

    const handleUpdate = useCallback((idx, delta) => {
        if (onUpdate) {
            onUpdate(value[idx], delta)
        } else {
            onChange([...value.map((v, i) => i !== idx ? v : { ...v, ...delta })]);
        }
    }, [onUpdate, onChange, value]);

    const handleDataListEntryChange = useCallback((delta, rowIdx) => {
        handleUpdate(rowIdx, delta)
    }, [handleUpdate]);

    const handleFooterChange = useCallback((delta) => 
        setNewRow(newRow => ({ ...newRow, ...delta }))
    , []);

    const deleteRow = useCallback((idx) => {
        if (onRemove) {
            onRemove(value[idx]);
        } else {
            onChange(value.filter((_, i) => idx !== i));
        }
    }, [value, onRemove, onChange]);

    const addRow = useCallback(() => {
        if (footerRef.current?.checkValidity()) {
            const toAdd = newRow;
            const initDelta = buildInitDelta(initialValueMapping, context);
            if (initDelta) {
                deepMerge(toAdd, initDelta);
            }
            if (onAdd) {
                onAdd(toAdd);
            } else {
                onChange([...ensureArray(value), toAdd]);
            }
            setNewRow({...defaultNewRow});
        }
    }, [newRow, value, defaultNewRow, onAdd, onChange, initialValueMapping, context]);

    if (onChange && (onAdd || onUpdate || onRemove)) {
        throw new Error("onChange and row events onAdd / onUpdte / onRemove are mutually exclusive - use one or the other");
    }

    return <>
        {displayRows.map((row, rowIdx) => <Row key={'dtlist_row_' + rowIdx} className="pb-2 mb-2" style={{ borderBottom: '1px solid lightgrey'}}>
            <Col xs="auto"><span className="d-block" style={{width: '20px', fontWeight: 'bold'}}>{ (rowIdx + 1).toString() + ')'}</span></Col>
            <Col><Row>{def.map((d, defIdx) => <DataListElement key={'dtlist_cell_' + rowIdx + '_' + defIdx} rowIdx={rowIdx} def={d} row={row} args={args} context={context} disabled={disabled} readonly={readonly} hideLabel={hideLabel} onChange={handleDataListEntryChange}></DataListElement>)}</Row></Col>
            <Col xs="auto">
                {disabled || disableRemove || (!onRemove && !onChange) ? null : <FloatingLabel placeholder=" "><DefaultButton kind="delete" style={{ width: '80px' }} confirmMessage={t('confirmDeleteItem', { item: rowIdx + 1 })} onClick={() => deleteRow(rowIdx)}></DefaultButton></FloatingLabel>}
            </Col>
        </Row>)}
        { disabled || disableAdd || (!onAdd && !onChange) ? null : <Row className="mt-2">
            <Col xs="auto"><span className="d-block" style={{width: '20px'}}>&nbsp;</span></Col>
            <Col>
                <ReactForm ref={footerRef}>
                    <Row>
                    {(footerDef ?? def).map(d => <DataListElement def={d} row={newRow} args={args} context={context} disabled={disabled} hideLabel={hideLabel} onChange={handleFooterChange}></DataListElement>)}
                    </Row>
                </ReactForm>
            </Col>
            <Col xs="auto">
                <FloatingLabel placeholder=" "><DefaultButton kind="add" style={{ width: '80px' }} onClick={addRow}></DefaultButton></FloatingLabel>
            </Col>
        </Row> }
    </>   
}, {

});

DataList.propTypes = {
    /**
     * A CustomForm definition (array of section objects with rows of CustomControls) defining the layout of the list entries
     */
    rowDef: PropTypes.array.isRequired,

    /**
     * An optional CustomForm definition (array of section objects with rows of CustomControls) defining the layout of the new
     * entry component of the list. Defaults to a blank rowDef if not set.
     */
    footerRowDef: PropTypes.array,

    /**
     * An object with default field values which are use to prepopulate the new row control with default values.
     */
    defaultNewRow: PropTypes.object,

    /**
     * An object of additional data that will be chained through to component controls generally used by
     * CustomForms to propagate complex additional information or defaults to deeply nested components
     */
    args: PropTypes.object,
    
    /**
     * An array of strings where each string is the name of a field for sort
     * ordering. Reverse ordering is achieved using a '-' prefix on the field name
     */
    defaultSort: PropTypes.arrayOf(PropTypes.string),

    /**
     * An array of objects representing the values of each form in the list
     */
    value: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)),

    /**
     * Called with the complete array of values behind the DataTable when any
     * change is made to the DataTable - you may only supply either onChange or
     * onAdd / onUpdate / onRemove
     */
    onChange: PropTypes.func,

    /**
     * Called with the object representing the row being added to the table
     */
    onAdd: PropTypes.func,

    /**
     * Called with the the row being updated, the field being updated, and the new value
     * to be stored into the field.
     */
    onUpdate: PropTypes.func,

    /**
     * Called with the row being removed
     */
    onRemove: PropTypes.func,

    /**
     * Collapses the floating label(s) used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Prohibit entires being added to the data list
     */
    disableAdd: PropTypes.bool,

    /**
     * Prohibit entires from being removed from the data list
     */
    disableRemove: PropTypes.bool,

    /**
     * Forces prompt to confirm deletion before proceeding
     */
    confirmDelete: PropTypes.bool
}

export default DataList;
